import type { ScopeRef } from "@enduring-systems/entitlements";
import { scopeKey, UNLIMITED } from "@enduring-systems/entitlements";
import { useEffect, useRef, useState } from "react";

import { ApiError } from "@/lib/api-client";

import { formatEntitlementValue } from "../effective";
import {
  applyBundle,
  type BlastRadius,
  type BundleMutationResult,
  retractBundle,
} from "../entitlements-api";
import type { OfferingCard } from "./offering-state";

/**
 * Applying and retracting an offering, apart from any component that draws it.
 *
 * Everything a surface needs to offer the action lives here: who may perform it
 * at a scope, what it would change, how a failure reads, and the call itself.
 * Separated from the card because ENR-1949's approve-and-apply performs the
 * same operation from the request queue, and an operator must not be told two
 * different things about one write depending on which panel they started from.
 *
 * What is deliberately NOT here: any claim about what is true at the scope now.
 * That comes from the ENR-1939 scope-state read, through `OfferingCard`, so a
 * mutation and the card beside it cannot disagree.
 */

/** Which direction an offering is being moved. */
export type OfferingAction = "apply" | "retract";

/**
 * The platform-staff keys a bundle write resolves against.
 *
 * Restated from `requiredPermissionsForScope` in identity because the browser
 * cannot import a service module. The server remains the authority: it demands
 * these at the BFF and re-demands the elevation at the layer that executes the
 * write, so this copy decides only whether the control READS as available.
 */
export const GRANT_PERMISSION = "entitlements:grant";
export const GRANT_WIDE_SCOPE_PERMISSION = "entitlements:grant_wide_scope";

/**
 * How far one write at this scope reaches.
 *
 * Exhaustive over `ScopeKind` rather than a pair of equality checks, so a scope
 * kind added to the engine breaks this build instead of silently inheriting the
 * narrow classification. The runtime default fails WIDE, because a kind this
 * console has never seen is not evidence that a write there is small.
 */
export function blastRadiusOf(scope: ScopeRef): BlastRadius {
  switch (scope.kind) {
    case "platform":
      return "platform";
    case "enterprise":
      return "enterprise";
    case "facility":
    case "user":
      return "scoped";
    default: {
      const unhandled: never = scope;
      void unhandled;
      return "platform";
    }
  }
}

function blastRadiusSentence(radius: BlastRadius): string {
  switch (radius) {
    case "platform":
      return "This scope is the platform root, so one write lands on every tenant.";
    case "enterprise":
      return "This scope is a whole workspace, so one write lands on every org unit and member inside it.";
    case "scoped":
      /* Not "this scope alone": resolution walks a principal's ancestor chain,
         so a grant at an org unit is in force for everyone whose chain includes
         it. Narrow is not the same as single. */
      return "This write lands on this scope, and applies to whoever resolves through it.";
  }
}

/**
 * Why this operator cannot apply or retract at this scope, or null when they
 * can.
 *
 * Returning the reason rather than a boolean is the point of the AC: a control
 * that is merely greyed out sends an operator to ask why in a support channel,
 * and one that looks available and then 403s sends them to ask whether the
 * write half-landed.
 */
export function offeringWriteBlock(scope: ScopeRef, permissions: readonly string[]): string | null {
  const radius = blastRadiusOf(scope);
  const required =
    radius === "scoped" ? [GRANT_PERMISSION] : [GRANT_PERMISSION, GRANT_WIDE_SCOPE_PERMISSION];
  const missing = required.filter((permission) => !permissions.includes(permission));
  if (missing.length === 0) return null;

  const label = missing.length === 1 ? "permission" : "permissions";
  return `${blastRadiusSentence(radius)} You are missing the ${missing.join(" and ")} ${label}.`;
}

/** One capability line in the confirmation, in the operator's words. */
export interface OfferingChangeLine {
  readonly capabilityId: string;
  readonly detail: string;
}

export interface OfferingChangePreview {
  readonly blastRadius: BlastRadius;
  readonly blastRadiusText: string;
  /** What the write moves. Empty when it would move nothing. */
  readonly lines: readonly OfferingChangeLine[];
  /**
   * Exceptions a retract leaves behind. Always empty for an apply, which never
   * touches an override.
   */
  readonly survivingOverrides: readonly OfferingChangeLine[];
  /**
   * The service would refuse this write outright, so the lines above describe
   * what it declined rather than what it would do. Only an increase can set
   * this: a quota member with nothing granted at the scope fails the whole
   * approval rather than being treated as a start from zero.
   */
  readonly refused?: boolean;
  /**
   * Why the write is refused, when the reason belongs to the OFFERING rather
   * than to any one capability.
   *
   * Separate from `lines` because a line's `capabilityId` is rendered as a
   * capability id and used as a React key and a testid, so an offering-level
   * reason forced into one would both mislead and risk colliding with a real
   * capability of the same name.
   */
  readonly refusalReason?: string;
}

/**
 * `type` decides the wording, not the runtime shape: the `unlimited` sentinel is
 * a plain string, so an enum member spelled that way must not read as an
 * uncapped quota. Null means the read named no type, and passing it through
 * empty lets the formatter fall back to the value's own shape rather than
 * asserting a type nothing declared.
 */
function valueText(type: string | null, value: unknown): string {
  return formatEntitlementValue(type ?? "", value);
}

/**
 * Exactly what this action would change at this scope, derived from the card and
 * therefore from the scope-state read.
 *
 * An apply reports what the TARGET version authors, and only that. Filtering to
 * authored members is not cosmetic: `card.members` also carries ids the
 * currently-applied bundle holds, so on a drifted scope (holds v1, catalog
 * publishes v2) an unfiltered list would present a v1-only capability as
 * something pressing "Apply v2" writes, when v2 never touches it and the stale
 * row survives untouched.
 *
 * A member the catalog does not author (the boolean publication derives from a
 * quota lever) is deliberately absent rather than listed without a value: the
 * catalog read carries only the levers, so the derived ids are unknowable here,
 * and the dialog says so instead of inventing them.
 *
 * A retract reports `card.removes`, derived to match what the store actually
 * deletes across every version. A capability with no bundle-sourced row
 * contributes no line, so the confirmation cannot promise a removal that will
 * not happen.
 */
export function describeOfferingChange(
  action: OfferingAction,
  card: OfferingCard,
  scope: ScopeRef
): OfferingChangePreview {
  const blastRadius = blastRadiusOf(scope);
  const base = { blastRadius, blastRadiusText: blastRadiusSentence(blastRadius) };

  if (action === "apply") {
    return {
      ...base,
      lines: card.members.flatMap((member) =>
        member.authored === null
          ? []
          : [
              {
                capabilityId: member.capabilityId,
                detail: `set to ${valueText(member.type, member.authored.value)}`,
              },
            ]
      ),
      survivingOverrides: [],
    };
  }

  return {
    ...base,
    lines: card.removes.map((removal) => ({
      capabilityId: removal.capabilityId,
      /* Naming the version that WROTE the row, not the one the card describes:
         on a scope straddling two versions that is the only way the list says
         which rows are which, and a retract takes them all. */
      detail: `removed, currently ${valueText(removal.row.type, removal.row.value)}, written by ${removal.row.provenance ?? "an unrecorded source"}`,
    })),
    survivingOverrides: card.removes.flatMap((removal) =>
      removal.survivingOverride === null
        ? []
        : [
            {
              capabilityId: removal.capabilityId,
              detail: `override of ${valueText(removal.survivingOverride.type, removal.survivingOverride.value)} stays in place`,
            },
          ]
    ),
  };
}

/**
 * What a QUOTA INCREASE would change at this scope (ENR-1947's other approval
 * shape), derived from the same card the apply preview is.
 *
 * Separate from `describeOfferingChange` because the service performs a
 * different write. `planApplyBundleIncrement` grows each quota lever to
 * `held + units * authored` and re-asserts every other member at its authored
 * value; it does NOT set the quota to the authored figure. Previewing an
 * increase with the apply's "set to" lines states a number the write never
 * produces, which on this surface is a privileged write confirmed against the
 * wrong values.
 *
 * The arithmetic mirrors `incrementedValue` deliberately and narrowly: either
 * side unlimited stays unlimited, a non-quota member is re-asserted, and a
 * quota with NOTHING granted here is a refusal rather than a start from zero.
 * That last case is the reason `refused` exists: the service fails the entire
 * approval, so a preview quietly showing arithmetic would promise a write that
 * cannot happen.
 */
export function describeOfferingIncrement(
  card: OfferingCard,
  scope: ScopeRef,
  units: number
): OfferingChangePreview {
  const blastRadius = blastRadiusOf(scope);
  let refused = false;

  /* The VERSION PIN, mirrored from `planApplyBundleIncrement`, which applies
     it before it plans a single op: the scope must hold exactly the one
     version this request names. An increment cannot change version, and it has
     no retirement pass, so the service refuses rather than writing one
     version's members while another version's stale rows survive.
     
     Checked here because base rows exist either way. A scope holding v1 while
     the request names v2 has rows to do arithmetic on, so a preview reading
     only the rows would show a from/to and leave the confirm enabled for a
     write that answers OFFERING_VERSION_MISMATCH. */
  const held = card.state.kind === "applied" ? card.state.heldVersions : [];
  if (held.length !== 1 || held[0] !== card.offering.version) {
    const heldText =
      held.length === 0 ? "is not applied at this scope" : `is applied here at ${held.join(", ")}`;
    return {
      blastRadius,
      blastRadiusText: blastRadiusSentence(blastRadius),
      lines: [],
      survivingOverrides: [],
      refused: true,
      refusalReason: `${card.offering.name} ${heldText}, and an increase cannot change version, so the service refuses it. Apply v${card.offering.version} first.`,
    };
  }

  const lines = card.members.flatMap((member): OfferingChangeLine[] => {
    if (member.authored === null) return [];
    if (member.type !== "quota") {
      return [
        {
          capabilityId: member.capabilityId,
          detail: `set to ${valueText(member.type, member.authored.value)}`,
        },
      ];
    }

    /* The BASE row, never the effective value. `readBaseRows` skips every
       override, so the service adds onto this row; with an override standing
       over it the two differ, and previewing the effective one would confirm
       a privileged write against numbers it never produces. */
    const held = member.base !== null && member.base.type === "quota" ? member.base.value : null;
    if (held === null) {
      refused = true;
      return [
        {
          capabilityId: member.capabilityId,
          detail: `nothing granted here to increase, so the service refuses the whole approval; apply ${card.offering.name} first`,
        },
      ];
    }
    if (held === UNLIMITED || member.authored.value === UNLIMITED) {
      return [{ capabilityId: member.capabilityId, detail: "stays unlimited" }];
    }
    /* `currentQuota` accepts the sentinel or a non-negative INTEGER and
       returns null for anything else, which fails the whole approval. Matched
       exactly here, so the preview cannot show arithmetic on a row the service
       will refuse to read as a quota. */
    if (
      !Number.isInteger(held) ||
      (held as number) < 0 ||
      typeof member.authored.value !== "number"
    ) {
      refused = true;
      return [
        {
          capabilityId: member.capabilityId,
          detail: `the value granted here cannot be increased, so what this approval writes cannot be shown`,
        },
      ];
    }
    const current = held as number;
    const added = units * member.authored.value;
    return [
      {
        capabilityId: member.capabilityId,
        detail: `increased by ${String(added)}, from ${String(current)} to ${String(current + added)}`,
      },
    ];
  });

  return {
    blastRadius,
    blastRadiusText: blastRadiusSentence(blastRadius),
    lines,
    survivingOverrides: [],
    refused,
  };
}

const ACTION_VERB: Readonly<Record<OfferingAction, string>> = {
  apply: "apply",
  retract: "retract",
};

/**
 * What to tell the operator when the write did not land.
 *
 * Every branch is operator copy, including the ones the service does not type.
 * The admin-api's bundle routes carry no blanket catch by design, so a DynamoDB
 * permission error or an exhausted partition read arrives as an untyped 500,
 * and relaying "Internal Server Error" would leave the one person who could act
 * on it with nothing to act on.
 *
 * The line every branch is sorted on is whether the request could have reached a
 * grant, because that decides whether "nothing changed" is a fact or a hope:
 *
 * - **Certainly not**: 403 (authorization precedes everything), 400 and 422
 *   (validation and every typed bundle failure precede the commit), and 503
 *   (the staff guard cannot read its allowlist, or the registry did not answer).
 *   These say nothing changed, because nothing did.
 * - **Unknown**: 500. It covers a store-side throw, and it covers a lambda-invoke
 *   transport failure that can land after the admin-api has already committed,
 *   and the client cannot tell those apart from a BFF resolver failure that
 *   never invoked anything. Claiming "nothing changed" here would be a guess
 *   about a privileged write, so this branch names the uncertainty instead.
 */
export function offeringFailureCopy(action: OfferingAction, cause: unknown): string {
  const verb = ACTION_VERB[action];
  const unknown = `Could not ${verb} this offering, and the outcome is not known. Re-read this scope before trying again: the write is transactional, so it either landed in full or not at all.`;
  /* A refusal the request never got past. Authorization and validation both run
     before any store call, so "nothing changed" is a fact here rather than a
     hope, and telling the operator to go re-read a scope nothing touched sends
     them looking for a change that cannot exist. */
  const refused = `so nothing changed at this scope.`;
  if (!(cause instanceof ApiError)) return unknown;

  switch (cause.code) {
    case "UNKNOWN_BUNDLE":
      return `This offering is not published at the version the catalog names, so there is nothing to ${verb}. The catalog and the published definitions are out of step; a deploy of the publication step reconciles them.`;
    case "BUNDLE_DEFINITION_INVALID":
      return `The published definition for this offering is no longer valid against the capability registry, so it was not applied. Nothing changed at this scope.`;
    case "BUNDLE_TOO_LARGE":
      return `This offering writes too many capabilities to move in one transaction, so it was not applied. Nothing changed at this scope.`;
    case "CONCURRENT_MUTATION":
      return `Something else changed while this ${verb} was in flight, so it was abandoned rather than applied over the top. Re-read this scope and try again.`;
    case "REGISTRY_UNAVAILABLE":
      return `The capability registry did not answer, so the ${verb} was not attempted. Nothing changed at this scope.`;
    /* The two ENR-2225 outcomes are worded apart on purpose. One is an answer
       and one is the absence of one, and an operator told their offering is
       misconfigured when the truth is that a stored row is unreadable goes and
       edits the wrong thing. The service's message names the capability and
       both numbers, so it is relayed rather than restated. */
    case "OFFERING_LOWERS_QUOTA":
      return `This offering would take capability away at this scope, so it was not applied: ${cause.message}. Nothing changed.`;
    case "QUOTA_IN_FORCE_UNKNOWN":
      return `What this scope currently holds could not be read, so the ${verb} was refused rather than risk lowering it: ${cause.message}. Nothing changed at this scope, and trying again will not help: a stored row needs correcting first.`;
    case "INVALID_SCOPE":
    case "INVALID_BUNDLE_REF":
    case "MALFORMED_REQUEST":
      return `The service refused this ${verb} as malformed, so nothing changed at this scope.`;
    default:
      break;
  }

  if (cause.status === 403) {
    return `You do not hold the permission this ${verb} needs at this scope, ${refused} ${cause.message}`;
  }
  if (cause.status === 400 || cause.status === 422) {
    return `The service refused this ${verb}: ${cause.message}. Nothing changed at this scope.`;
  }
  if (cause.status === 503) {
    /* Every 503 on this path is a pre-write refusal: the staff guard answers it
       when the allowlist cannot be read, and the store answers it as
       REGISTRY_UNAVAILABLE, which the switch above already worded. Neither has
       reached a grant. */
    return `The service could not authorize this ${verb} right now, ${refused} Try again shortly.`;
  }
  return unknown;
}

export interface UseOfferingMutationInput {
  /**
   * Where the write lands. Not nullable: a surface with no scope has nothing to
   * apply an offering to, so it should not be holding this hook at all, and an
   * accepted null would make "did nothing" indistinguishable from "wrote".
   */
  readonly scope: ScopeRef;
  /** Fired after a write lands, so the page re-reads what now applies. */
  readonly onMutated: () => void;
}

export interface UseOfferingMutationReturn {
  /** The action currently in flight, or null. */
  readonly pending: OfferingAction | null;
  /** Operator copy for the last failure at the scope on screen. */
  readonly error: string | null;
  /** The last outcome at the scope on screen, in the operator's words. */
  readonly result: string | null;
  /**
   * Settles when the write has, so a confirmation can stay up until then and
   * come down once, whichever way it went. Never rejects: a failure is reported
   * through `error`, and a caller awaiting this must not have to catch as well.
   *
   * `intendedScope` is the scope the CALLER was showing when the operator chose
   * this action, and it is required rather than optional so a caller cannot
   * forget to say. If it is no longer the scope on screen the write is refused,
   * not retargeted: see the guard in `run`.
   */
  readonly run: (
    action: OfferingAction,
    offering: OfferingRef,
    intendedScope: ScopeRef
  ) => Promise<void>;
}

/** The offering a write names, at the version the operator was shown. */
export interface OfferingRef {
  readonly name: string;
  readonly version: string;
}

function summarize(
  action: OfferingAction,
  result: BundleMutationResult,
  offering: OfferingRef
): string {
  const id = `${offering.name}@${offering.version}`;
  if (action === "retract") {
    // A retract of an offering that is not applied is a successful no-op. Every
    // reported change IS a removal, so the raw length is the honest count.
    if (result.changes.length === 0) {
      return `${offering.name} was not applied here, so nothing changed.`;
    }
    const noun = result.changes.length === 1 ? "capability" : "capabilities";
    return `Retracted ${offering.name}: ${String(result.changes.length)} ${noun} removed.`;
  }
  /* The COUNT of members, never a count of what moved: an apply reports one
     entry per member unconditionally, so re-applying an offering already in
     force returns a full list in which every before equals its after. */
  const noun = result.changes.length === 1 ? "member row" : "member rows";
  return `Applied ${id}: ${String(result.changes.length)} ${noun} written.`;
}

/**
 * Run one apply or retract against the selected scope.
 *
 * The scope on screen is mirrored during RENDER, not in an effect: effects run
 * after paint, so a response that resolves in the gap between the commit and
 * the effect would still match the previous scope, be accepted, and refresh the
 * page around an outcome describing somewhere else. On this page that would
 * report one customer's write under another customer's name.
 *
 * Nothing here is optimistic. `onMutated` re-reads the scope, and the card
 * re-derives its state from that read, so what the operator sees after a write
 * is the engine's answer rather than what this hook believes it wrote.
 */
export function useOfferingMutation({
  scope,
  onMutated,
}: UseOfferingMutationInput): UseOfferingMutationReturn {
  const currentKey = scopeKey(scope);
  const shownFor = useRef(currentKey);
  shownFor.current = currentKey;
  /** Held for the duration of a write, so a second cannot start beside it. */
  const inFlight = useRef(false);

  const [pending, setPending] = useState<OfferingAction | null>(null);
  const [outcome, setOutcome] = useState<{
    readonly scopeKey: string;
    readonly error: string | null;
    readonly result: string | null;
  } | null>(null);

  /* A returning scope must not resurrect an old outcome: the page has re-read
     since, so it would read as something that just happened.

     Keyed on the scope KEY rather than the ScopeRef, because the scope comes
     from a read whose response is a new object each time. Depending on identity
     would clear the outcome on the very re-read the write just triggered, so
     the operator would watch the result of their own action disappear. */
  useEffect(() => {
    setOutcome(null);
    setPending(null);
  }, [currentKey]);

  const run = async (
    action: OfferingAction,
    offering: OfferingRef,
    intendedScope: ScopeRef
  ): Promise<void> => {
    /* The scope the operator was shown, checked against the scope on screen NOW.
       A confirmation carries a preview of one scope's rows, and the press
       arrives later, so without this the write lands wherever the page has since
       moved to while every line the operator read described somewhere else. On
       this surface that is a privileged write at a workspace nobody was looking
       at, and a retract there removes rows.

       Refused, never retargeted: the preview belongs to the other scope, so
       silently repointing the write is the dishonest option. Checked HERE rather
       than cleared in an effect because an effect runs after paint and so races
       the press. */
    if (scopeKey(intendedScope) !== currentKey) {
      setOutcome({
        scopeKey: currentKey,
        error: `The scope changed while this confirmation was open, so nothing was written. Re-open the action against the scope now on screen.`,
        result: null,
      });
      return;
    }
    /* One write at a time, gated on a REF rather than on `pending`.
       A second write started before the first settles would report its outcome
       over the top of one the operator has not read yet, and on a privileged
       write it would also double the audit rows and the wide-scope alarm. State
       cannot hold that line: the guard would be read from the render that
       created this closure, so anything firing before `setPending` commits sees
       the stale value. A ref is written synchronously and closes the window
       whatever the commit timing. */
    if (inFlight.current) return;
    inFlight.current = true;
    const issuedKey = currentKey;
    setPending(action);
    setOutcome(null);
    const call =
      action === "apply"
        ? applyBundle({ scope, bundleName: offering.name, version: offering.version })
        : retractBundle({ scope, bundleName: offering.name });

    await call
      .then((value) => {
        if (shownFor.current !== issuedKey) return;
        setOutcome({
          scopeKey: issuedKey,
          error: null,
          result: summarize(action, value, offering),
        });
        onMutated();
      })
      .catch((cause: unknown) => {
        if (shownFor.current !== issuedKey) return;
        setOutcome({
          scopeKey: issuedKey,
          error: offeringFailureCopy(action, cause),
          result: null,
        });
      })
      .finally(() => {
        /* Released unconditionally, unlike the state updates above. Those are
           skipped for an abandoned scope because they would paint somewhere
           else, but leaving the ref held would latch the hook into refusing
           every later write. */
        inFlight.current = false;
        if (shownFor.current !== issuedKey) return;
        setPending(null);
      });
  };

  /* Painted only against the scope it belongs to. The guard above stops a late
     response being stored; this stops a stored one being shown under a scope it
     does not describe, whatever the timing. */
  const shown = outcome !== null && outcome.scopeKey === currentKey ? outcome : null;

  return { pending, error: shown?.error ?? null, result: shown?.result ?? null, run };
}
